import React, { FC } from 'react';
import { Box, DialogActions, DialogContent, Grid } from '@material-ui/core';
import DialogWithCloseButton from '../DialogWithCloseButton/DialogWithCloseButton';
import IconLightBulb from '../Icons/IconLightBulb/IconLightBulb';
import TypographyWrapper, { TypographyWeight } from '../TypographyWrapper/TypographyWrapper';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import IconSpinner from '../Icons/IconSpinner/IconSpinner';
import ErrorComponent from '../Error/Error';
import Divider from '../Divider/Divider';
import Button from '../Button/Button';
import css from './BackgroundRemovalModal.module.css';
import { useIsMobile } from '../../hooks/useIsMobile';
import { getHelperTextFromError } from './backgroundRemovalUtils';

interface BackgroundRemovalModalBaseProps {
  open: boolean;
  onClose: () => void;
  onModalSubmit: () => void;
  imageSrc: string;
  isUploading?: boolean;
  hasUploadError: boolean;
  isRemovingBackground?: boolean;
  removeBackgroundError: string | null;
  helperText: string;
  children?: React.ReactNode;
}

const BackgroundRemovalModalBase: FC<BackgroundRemovalModalBaseProps> = (
  props: BackgroundRemovalModalBaseProps
) => {
  const {
    open,
    onClose,
    onModalSubmit,
    imageSrc,
    isUploading,
    hasUploadError,
    isRemovingBackground,
    removeBackgroundError,
    helperText,
    children,
  } = props;

  const isMobile = useIsMobile();

  const hasError = !!removeBackgroundError || hasUploadError;

  return (
    <DialogWithCloseButton open={open} onClose={onClose} titleText="Remove Background">
      <Divider className={css.divider} />
      <Box
        m={2}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        maxWidth="400px"
      >
        <Box>
          <IconLightBulb className={css.iconLightBulb} />
        </Box>
        <TypographyWrapper
          variant="body1"
          weight={TypographyWeight.Bold}
          typographyOverrides={{ display: 'inline' }}
        >
          Seller Tip: {helperText}
        </TypographyWrapper>
      </Box>
      <DialogContent
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {!isRemovingBackground && !removeBackgroundError && (
          <Box
            px={2}
            maxWidth="400px"
            maxHeight={isMobile ? '400px' : undefined}
            display="flex"
            justifyContent="center"
          >
            <ResponsiveImage className={css.imageContainer} alt="Uploaded Image" src={imageSrc} />
          </Box>
        )}
        {isRemovingBackground && !removeBackgroundError && (
          <Box className={css.spinnerContainer} display="flex" justifyContent="center" flexGrow={1}>
            <IconSpinner />
          </Box>
        )}
        {removeBackgroundError && (
          <Box p={2} textAlign="center">
            <ErrorComponent>{getHelperTextFromError(removeBackgroundError)}</ErrorComponent>
          </Box>
        )}
        {hasUploadError && (
          <Box p={2} textAlign="center">
            <ErrorComponent>
              Error uploading image. Please try again or contact support@treet.co.
            </ErrorComponent>
          </Box>
        )}
      </DialogContent>
      <Box px={3} mb={2}>
        {children}
      </Box>
      <Divider className={css.divider} />
      <DialogActions>
        <Grid container spacing={2} justifyContent="flex-end" alignItems="center">
          <Grid item xs={5}>
            <Box p={2}>
              <Button
                disabled={isRemovingBackground || isUploading || hasError}
                onClick={() => {
                  onModalSubmit();
                }}
                inProgress={isUploading}
              >
                Save
              </Button>
            </Box>
          </Grid>
        </Grid>
      </DialogActions>
    </DialogWithCloseButton>
  );
};

export default BackgroundRemovalModalBase;
